// react
import React from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import { Avatar } from "@performyard/py-components";

import Typography, { BaseProps as TypographyProps } from "../Text";
import TruncatedText from "../Text/TruncatedText";
// styles
import css from "./Chip.module.scss";

export type Color = "default" | "blue" | "green" | "red" | "orange" | "yellow" | "paleYellow" | "neutral";

export declare interface ChipProps {
	/**
	 * The label to display on the chip.
	 */
	label?: string | React.ReactNode;
	/**
	 * If provided, this is the employee id for the avatar image.
	 */
	avatarID?: string;
	/**
	 * Any additional classnames to add to the chip.
	 */
	className?: string;
	/**
	 * innerClassName of the chip
	 */
	innerStyle?: React.CSSProperties;
	/**
	 * The color of the chip.
	 * @default default
	 */
	color?: Color;
	/**
	 * Max width of the chip to handle truncation
	 */
	maxWidth?: number;
	/**
	 * If true, the chip will be rendered with an active state.
	 */
	isActive?: boolean;
	/**
	 * Icon rendered on left side of chip
	 */
	leftIcon?: IconDefinition;
	/**
	 * Icon rendered on right side of chip
	 */
	rightIcon?: IconDefinition;
	/**
	 * Callback function for when the chip right icon is clicked
	 */
	onRightIconClick?: () => void;
	/**
	 * Callback function for when the chip left icon is clicked
	 */
	onLeftIconClick?: () => void;

	size?: string;
	/**
	 * Size of the chip
	 * @default default
	 */
	labelProps?: Partial<TypographyProps>;
}

/**
 * @deprecated use py-components chip instead
 * Chip component used to display things like tags, filters, bulk assignment,
 * etc. It can be enhanced with an avatar and a delete button to handle any
 * specific use case.
 */
const Chip = (props: ChipProps) => {
	const {
		label = "",
		avatarID,
		color = "default",
		maxWidth = null,
		className = "",
		innerStyle = {},
		isActive = false,
		leftIcon = null,
		rightIcon = null,
		onRightIconClick,
		onLeftIconClick,
		labelProps = {},
	} = props;

	const hasAvatar = !!avatarID;
	const avatarProps = { id: avatarID };

	return (
		<div className={`${css.chipContainer} ${className}`}>
			<div
				className={` ${css.chip} ${css[`color-${color}`]} ${isActive ? css.active : ""} max-w-full`}
				style={{ ...innerStyle }}
			>
				<div data-testid="inner-chip" className={`${css.innerChip} max-w-full`}>
					{leftIcon && !hasAvatar && (
						<div
							onClick={onLeftIconClick}
							className={` ${css.chipIcon} ${onLeftIconClick ? css.hoverable : ""} flex-shrink-0`}
						>
							<FontAwesomeIcon
								data-testid={leftIcon?.iconName}
								icon={leftIcon}
								className={css.leftIcon}
							/>
						</div>
					)}
					<div
						data-testid="chip-label"
						className={`${css.chipLabel} ${hasAvatar ? css.hasAvatar : ""} min-w-0 flex-1`}
					>
						{maxWidth ? (
							<TruncatedText size="paragraph-3" weight="regular" text={label} maxWidth={maxWidth} />
						) : (
							<Typography
								size="paragraph-3"
								weight="regular"
								color={isActive ? "neutral-1" : "neutral-9"}
								{...labelProps}
							>
								{label}
							</Typography>
						)}
					</div>
					{rightIcon && (
						<div
							onClick={onRightIconClick}
							className={` ${css.chipIcon} ${onRightIconClick ? css.hoverable : ""} ${isActive ? css.active : ""} flex-shrink-0`}
						>
							<FontAwesomeIcon data-testid={rightIcon?.iconName} icon={rightIcon} />
						</div>
					)}
				</div>
			</div>
			{hasAvatar ? (
				<Avatar size={28} dangerouslySetClassName={css.chipAvatar} alt="" testId="avatar" {...avatarProps} />
			) : null}
		</div>
	);
};

/** @deprecated use py-components chip instead */
export default Chip;
